/**
 * Purchase stock Redux helpers (HPRMS Phase D).
 * Boot = page-1 lists; Sync = full pager; open = Redux; hole-fill detail; write = upsert.
 */

import {
  getGoodsReceipts,
  getGoodsReceiptById,
  getQualityInspections,
  getQualityInspectionById,
  getPurchaseReturns,
  getPurchaseReturnById,
  syncGoodsReceipts,
  syncQualityInspections,
  syncPurchaseReturns,
  STOCK_LIST_PAGE_SIZE,
} from "../apis/purchaseStock.api";

const getStore = () => require("../index").default;

const entityKey = (row) => {
  if (!row || typeof row !== "object") return "";
  for (const v of [row.POID, row.poid, row.id]) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s && s !== "null" && s !== "undefined" && s !== "0") return s;
  }
  return "";
};

const hasLines = (row) => {
  const lines = row?.lineItems || row?.goodsReceiptLines || row?.purchaseReturnLines;
  return Array.isArray(lines) && lines.length > 0;
};

// ─── Goods Receipts ────────────────────────────────────────────────

export const goodsReceiptsPresentInStore = (stockState) => {
  const state = stockState || getStore().getState()?.purchaseStockState;
  return Boolean(state?.grnsSyncedAt);
};

export const findGoodsReceiptInState = (stockState, id) => {
  const key = String(id ?? "").trim();
  if (!key || key === "null") return null;
  const list = stockState?.goodsReceipts || [];
  return list.find((g) => entityKey(g) === key) || null;
};

export const upsertGoodsReceiptInStore = (grn) => {
  if (!grn) return;
  getStore().dispatch({ type: "UPSERT_GOODS_RECEIPT", goodsReceipt: grn });
};

export const applyGoodsReceiptsToStore = (list) => {
  getStore().dispatch({
    type: "GET_GOODS_RECEIPTS",
    goodsReceipts: Array.isArray(list) ? list : [],
  });
};

export const refreshGoodsReceiptsInStore = async ({
  start = 0,
  size = STOCK_LIST_PAGE_SIZE,
  silent = true,
  context = "Loading Goods Receipts",
} = {}) => {
  const list = await getGoodsReceipts({
    start,
    size,
    silent,
    hideLoader: silent,
  });
  applyGoodsReceiptsToStore(list);
  return list;
};

export const refreshAllGoodsReceiptsInStore = async () => {
  const list = await syncGoodsReceipts();
  applyGoodsReceiptsToStore(list);
  return list;
};

export const ensureGoodsReceiptsInStore = async ({
  force = false,
  silent = true,
} = {}) => {
  if (!force && goodsReceiptsPresentInStore()) {
    return getStore().getState()?.purchaseStockState?.goodsReceipts || [];
  }
  return refreshGoodsReceiptsInStore({ silent });
};

export const resolveGoodsReceiptById = async (id, { forceNetwork = false } = {}) => {
  const key = String(id ?? "").trim();
  if (!key || key === "null") return null;
  const store = getStore();
  if (!forceNetwork) {
    const fromStore = findGoodsReceiptInState(
      store.getState()?.purchaseStockState,
      key
    );
    if (hasLines(fromStore)) return fromStore;
  }
  const full = await getGoodsReceiptById(key);
  if (!full) return null;
  const stamped = { ...full, __detailFetched: true };
  upsertGoodsReceiptInStore(stamped);
  return stamped;
};

// ─── Quality Inspections ───────────────────────────────────────────

export const qualityInspectionsPresentInStore = (stockState) => {
  const state = stockState || getStore().getState()?.purchaseStockState;
  return Boolean(state?.qiSyncedAt);
};

export const findQualityInspectionInState = (stockState, id) => {
  const key = String(id ?? "").trim();
  if (!key || key === "null") return null;
  const list = stockState?.qualityInspections || [];
  return list.find((q) => entityKey(q) === key) || null;
};

export const upsertQualityInspectionInStore = (qi) => {
  if (!qi) return;
  getStore().dispatch({
    type: "UPSERT_QUALITY_INSPECTION",
    qualityInspection: qi,
  });
};

export const applyQualityInspectionsToStore = (list) => {
  getStore().dispatch({
    type: "GET_QUALITY_INSPECTIONS",
    qualityInspections: Array.isArray(list) ? list : [],
  });
};

export const refreshQualityInspectionsInStore = async ({
  start = 0,
  size = STOCK_LIST_PAGE_SIZE,
  silent = true,
} = {}) => {
  const list = await getQualityInspections({
    start,
    size,
    silent,
    hideLoader: silent,
  });
  applyQualityInspectionsToStore(list);
  return list;
};

export const refreshAllQualityInspectionsInStore = async () => {
  const list = await syncQualityInspections();
  applyQualityInspectionsToStore(list);
  return list;
};

export const ensureQualityInspectionsInStore = async ({
  force = false,
  silent = true,
} = {}) => {
  if (!force && qualityInspectionsPresentInStore()) {
    return getStore().getState()?.purchaseStockState?.qualityInspections || [];
  }
  return refreshQualityInspectionsInStore({ silent });
};

export const resolveQualityInspectionById = async (
  id,
  { forceNetwork = false } = {}
) => {
  const key = String(id ?? "").trim();
  if (!key || key === "null") return null;
  const store = getStore();
  if (!forceNetwork) {
    const fromStore = findQualityInspectionInState(
      store.getState()?.purchaseStockState,
      key
    );
    if (hasLines(fromStore)) return fromStore;
  }
  const full = await getQualityInspectionById(key);
  if (!full) return null;
  const stamped = { ...full, __detailFetched: true };
  upsertQualityInspectionInStore(stamped);
  return stamped;
};

// ─── Purchase Returns ──────────────────────────────────────────────

export const purchaseReturnsPresentInStore = (stockState) => {
  const state = stockState || getStore().getState()?.purchaseStockState;
  return Boolean(state?.returnsSyncedAt);
};

export const findPurchaseReturnInState = (stockState, id) => {
  const key = String(id ?? "").trim();
  if (!key || key === "null") return null;
  const list = stockState?.purchaseReturns || [];
  return list.find((r) => entityKey(r) === key) || null;
};

export const upsertPurchaseReturnInStore = (pr) => {
  if (!pr) return;
  getStore().dispatch({ type: "UPSERT_PURCHASE_RETURN", purchaseReturn: pr });
};

export const applyPurchaseReturnsToStore = (list) => {
  getStore().dispatch({
    type: "GET_PURCHASE_RETURNS",
    purchaseReturns: Array.isArray(list) ? list : [],
  });
};

export const refreshPurchaseReturnsInStore = async ({
  start = 0,
  size = STOCK_LIST_PAGE_SIZE,
  silent = true,
} = {}) => {
  const list = await getPurchaseReturns({
    start,
    size,
    silent,
    hideLoader: silent,
  });
  applyPurchaseReturnsToStore(list);
  return list;
};

export const refreshAllPurchaseReturnsInStore = async () => {
  const list = await syncPurchaseReturns();
  applyPurchaseReturnsToStore(list);
  return list;
};

export const ensurePurchaseReturnsInStore = async ({
  force = false,
  silent = true,
} = {}) => {
  if (!force && purchaseReturnsPresentInStore()) {
    return getStore().getState()?.purchaseStockState?.purchaseReturns || [];
  }
  return refreshPurchaseReturnsInStore({ silent });
};

export const resolvePurchaseReturnById = async (
  id,
  { forceNetwork = false } = {}
) => {
  const key = String(id ?? "").trim();
  if (!key || key === "null") return null;
  const store = getStore();
  if (!forceNetwork) {
    const fromStore = findPurchaseReturnInState(
      store.getState()?.purchaseStockState,
      key
    );
    if (hasLines(fromStore)) return fromStore;
  }
  const full = await getPurchaseReturnById(key);
  if (!full) return null;
  const stamped = { ...full, __detailFetched: true };
  upsertPurchaseReturnInStore(stamped);
  return stamped;
};

/** Returns linked to a GRN from Redux (may be headers only). */
export const purchaseReturnsForGrn = (stockState, grnId) => {
  const key = String(grnId ?? "");
  if (!key) return [];
  return (stockState?.purchaseReturns || []).filter(
    (r) => String(r.GoodsReceipt_Id || r.goodsReceipt_Id || "") === key
  );
};

/** GRNs linked to a PO from Redux. */
export const goodsReceiptsForPurchaseOrder = (stockState, purchaseOrderId) => {
  const key = String(purchaseOrderId ?? "");
  if (!key) return [];
  return (stockState?.goodsReceipts || []).filter(
    (g) =>
      String(g.PurchaseOrder_Id || g.purchaseOrder_Id || g.purchaseOrderId || "") ===
      key
  );
};
